import { useState } from "react";
import { toast } from "sonner";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useCreateShare } from "@/hooks/use-shares";
import type { Album, ProjectDetail } from "@/schemas/photos";
import type { ShareSummary } from "@/schemas/shares";

const FUTURE_OPTIONS = [
  { value: "yes", label: "Shared too" },
  { value: "no", label: "Not shared" },
] as const;

/** Share an album, or with no album the whole project, with one IE firm. */
export function ShareDialog({
  open,
  onOpenChange,
  project,
  album,
  audiences,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
  album?: Album;
  audiences: ShareSummary["audiences"];
}) {
  const [picked, setPicked] = useState("");
  const [future, setFuture] = useState("yes");
  const createShare = useCreateShare();

  const audience = audiences.find((a) => a.key === picked) ?? audiences[0];
  const name = album ? album.name : project.name;
  const count = album ? album.photoCount : project.photoCount;

  function share() {
    if (!audience) return;
    createShare.mutate(
      {
        projectId: project.id,
        audience: audience.label,
        albumId: album?.id,
        effect: "include",
        includeFuture: future === "yes",
      },
      {
        onSuccess: () => {
          toast.success(`Shared ${name} with ${audience.label}`);
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>{album ? "Share album" : "Share whole project"}</DialogTitle>
          <DialogDescription>
            {name}, {count.toLocaleString("en-US")} {count === 1 ? "photo" : "photos"}. Photos you
            have excluded stay excluded.
          </DialogDescription>
        </DialogHeader>
        {audience ? (
          <div className="space-y-4">
            {audiences.length > 1 ? (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Share with</Label>
                <EnumSelect
                  aria-label="Share with"
                  value={audience.key}
                  onValueChange={setPicked}
                  options={audiences.map((a) => ({ value: a.key, label: a.label }))}
                  className="w-full"
                />
              </div>
            ) : (
              <p className="text-sm">
                Share with <span className="font-medium">{audience.label}</span>
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Photos added later</Label>
              <EnumSelect
                aria-label="Photos added later"
                value={future}
                onValueChange={setFuture}
                options={FUTURE_OPTIONS}
                className="w-full"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            There is no IE firm to share with yet. An admin adds one in Settings, Users.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="share-confirm"
            disabled={!audience || createShare.isPending}
            onClick={share}
          >
            Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
